// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// One row per IR flag. Makes the gate-blocking status legible at a glance: a blocking,
// unresolved flag is the thing standing between the cycle and sign-off. Optional resolve
// affordance is wired by the caller (GateTab / FlagsTab) to resolve_reporting_flag.

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CircleAlert, Ban, Info } from "lucide-react";
import type { Flag } from "@/types/ir";

// A clearance is only real if it names WHO made it. Gate leg (ii-b) four-eyes counts a
// resolved flag whose resolved_by is not a uuid as "cleared by you, or by nobody
// identifiable" and REFUSES the signature outright — so an unattributed clearance is not a
// cleared flag, it is a JAMMED gate. This row used to paint every resolved flag with the
// same reassuring primary-tone "resolved" badge, which meant the surface showed five calm
// green ticks over the precise reason cycle ec8e622a could not be signed by anyone.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function FlagRow({
  flag,
  onResolve,
  resolving = false,
  className,
}: {
  flag: Flag;
  onResolve?: (flag: Flag) => void;
  resolving?: boolean;
  className?: string;
}) {
  const resolvedBy = String(flag.resolved_by ?? "").trim();
  const namedResolver = flag.resolved === true && UUID_RE.test(resolvedBy);
  // resolved, but by nobody identifiable — and it still carries gate weight.
  const unattributed = flag.resolved === true && !namedResolver;
  const jamsGate = unattributed && flag.blocks_gate === true;

  const blocking = flag.blocks_gate && !flag.resolved;
  const Icon = namedResolver
    ? CheckCircle2
    : jamsGate
      ? Ban
      : unattributed
        ? CircleAlert
        : blocking
          ? Ban
          : flag.severity === "warn"
            ? CircleAlert
            : Info;
  const iconTone = namedResolver
    ? "text-primary"
    : jamsGate || blocking
      ? "text-destructive"
      : unattributed || flag.severity === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";

  return (
    <Card
      className={cn(
        "p-3 flex items-start justify-between gap-3",
        (blocking || jamsGate) && "border-destructive/40",
        className,
      )}
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", iconTone)} aria-hidden />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-body text-sm font-medium text-foreground">{flag.id}</span>
            <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-muted text-muted-foreground">{flag.kind}</span>
            {blocking && (
              <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-destructive/10 text-destructive">
                blocks gate
              </span>
            )}
            {namedResolver && (
              <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-primary/10 text-primary">resolved</span>
            )}
            {unattributed && (
              <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                marked resolved, no named resolver
              </span>
            )}
            {jamsGate && (
              <span className="font-body text-[11px] rounded px-1.5 py-0.5 bg-destructive/10 text-destructive">
                gate will refuse
              </span>
            )}
          </div>
          <p className="font-body text-xs text-muted-foreground mt-1">{flag.message}</p>
          {jamsGate && (
            <p className="font-body text-xs text-destructive mt-1">
              This flag is marked resolved but records no identifiable resolver, and it still carries gate weight. The
              four-eyes check reads that as cleared by nobody, so sign-off will be refused for every approver until the
              flag is retired properly. Re-run the producing stage so the flag is superseded on the audited path, or have
              a named Investment or Operations reviewer resolve it.
            </p>
          )}
          <p className="font-body text-[11px] text-muted-foreground/60 mt-0.5 truncate" title={flag.target}>
            {flag.target}
          </p>
        </div>
      </div>
      {blocking && onResolve && (
        <Button variant="outline" size="sm" disabled={resolving} onClick={() => onResolve(flag)} className="shrink-0">
          {resolving ? "Resolving…" : "Resolve"}
        </Button>
      )}
    </Card>
  );
}
